import VideoComponent from './Video';
import Play from './Play';
import Views from './Views';
import Likes from './Likes';
import { useRef, useState } from 'react';
import SbUtils from '../../../../Utils/SbUtils';
import { useGdpr } from '../../../../Frontend/Hooks/GdprContext';

const getThumbnailStyle = (thumbnailSize, postStyle, layout, isPlaceholder) => ({
    position: 'relative',
    width: thumbnailSize === 'large' ? '385px' : '100%',
    height: ['large', 'portrait'].includes(thumbnailSize) || layout === 'masonry' ? '100%' : '269px',
    overflow: 'hidden',
    borderRadius: postStyle === "regular" ? '8px' : '0px',
    background: isPlaceholder ? '#E8E8E8' : 'black',
    margin: 'auto',
});

/**
 * Get the image src respecting GDPR consent state.
 *
 * - Local images are always safe (hosted on your server).
 * - Remote images (cover_image_url) are only used when consent is given or GDPR is not active.
 * - Falls back to placeholder when GDPR is active and no consent.
 */
const getImageSrc = (post, gdprActive, gdprConsented, gdprPlaceholder) => {
    // Local images are always GDPR-safe.
    if (post?.local_cover_image_url) {
        return post.local_cover_image_url;
    }

    // If GDPR is not active or consent was given, use the remote image.
    if (!gdprActive || gdprConsented) {
        return post?.cover_image_url;
    }

    // GDPR active, no consent, no local image: use placeholder.
    // Never fall back to CDN image — that would defeat GDPR protection.
    return gdprPlaceholder || '';
};

const PostImage = ({
    isPro,
    post,
    feedSettings,
    activeIndex,
    setActiveIndex,
    postIndex,
    isPlaying,
    setIsPlaying,
    layout,
    handlePostClick,
    isFeedEditor,
}) => {
    const { gdprActive, gdprConsented, gdprPlaceholder } = useGdpr();
    const thumbnailSize = layout === 'list' ? 'large' : (feedSettings?.feedTemplate === 'widget' ? 'portrait' : 'medium');
    const postStyle = feedSettings?.postStyle || 'regular';
    const playerExperience = (isPro && feedSettings?.videoPlayer) || 'lightbox';
    const noMetaClass = (isPro && (feedSettings?.postElements?.includes('views') || feedSettings?.postElements?.includes('likes'))) ? '' : 'sb-no-meta';

    const [dynamicHeight, setDynamicHeight] = useState('inherit');
    const imageWrapRef = useRef(null);

    // Block inline video when GDPR is active and no consent.
    const canPlayVideo = !gdprActive || gdprConsented;

    const handleImageClick = () => {
        if (!isFeedEditor) {
            if (playerExperience === 'lightbox' || layout === 'gallery') {
                handlePostClick(postIndex);
            } else if (canPlayVideo) {
                setIsPlaying(true);
                setActiveIndex(postIndex);
            } else if (post?.share_url) {
                // GDPR blocks inline playback — open the post on TikTok instead.
                window.open(post.share_url, '_blank', 'noopener,noreferrer');
            }
            if (imageWrapRef.current) {
                const height = imageWrapRef.current.clientHeight;
                setDynamicHeight(`${height}px`);
            }
        }
    };

    const imageSrc = getImageSrc(post, gdprActive, gdprConsented, gdprPlaceholder);
    const isPlaceholder = gdprActive && !gdprConsented && !post?.local_cover_image_url;

    return (
        <div
            ref={imageWrapRef}
            className='sb-post-item-image-wrap'
            style={getThumbnailStyle(thumbnailSize, postStyle, layout, isPlaceholder)}
            onClick={handleImageClick}
        >

            {isPlaying && isPro && post?.video_url && canPlayVideo && activeIndex === postIndex && layout !== 'gallery' ? (
                <VideoComponent
                    post={post}
                    playerExperience={playerExperience}
                    dynamicHeight={dynamicHeight}
                />
            ) : (
                <img
                    className='sb-post-item-image'
                    src={imageSrc}
                    alt={SbUtils.generateAltText(post)}
                />
            )}

            {(layout === 'gallery' || ((!isPlaying || activeIndex !== postIndex) && layout !== 'list')) && (
                <>
                    {feedSettings?.postElements?.includes('playIcon') &&
                        <Play className={noMetaClass} />
                    }

                    <div className='sb-post-item-meta'>
                        {isPro && feedSettings?.postElements?.includes('views') &&
                            <Views post={post} />
                        }

                        {isPro && feedSettings?.postElements?.includes('likes') &&
                            <Likes post={post} />
                        }
                    </div>
                </>
            )}
        </div>
    );
}

export default PostImage;
